import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Progress from 'core/components/progress/Progress'
import Card from 'core/elements/card'
import Grid from 'core/elements/grid'
import Text from 'core/elements/Text'
import ErrorModal from '../ErrorModal'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { getPortsForVm } from 'openstack/components/networks/actions'
import TasksPage from './TasksPage'
import OverviewCard from './OverviewCard'
import VmStateCellComponent from '../VmStateCellComponent'
import { isNilOrEmpty } from 'utils/fp'
import { Labels } from 'k8s/components/common/entity/labels-and-annotations/LabelsOrAnnotations'
import Tooltip from 'core/elements/tooltip'
import { humanReadableSize } from 'openstack/helpers'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { IVolumeDetailsPageTabs } from 'openstack/components/storage/volumes/volume-details/model'
import { routeManager } from 'core/utils/routes'
import SimpleLink from 'core/components/SimpleLink'
import { getResmgrRoleConfig } from 'openstack/components/infrastructure/actions'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'
import { prop } from 'ramda'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { formatTableDate } from 'utils/misc'
import VmErrorModal from '../VmErrorModal'
import { virtualMachinesByIdSelector } from 'app/plugins/openstack/components/vms/selectors'
import { volumeSnapshotsByIdSelector } from 'app/plugins/openstack/components/storage/snapshots/selectors'
import { volumesByIdSelector } from 'app/plugins/openstack/components/storage/volumes/selectors'
import { getVmSnapshotColumns } from 'app/plugins/openstack/components/images/helpers'
import { getVolumeSnapshotColumns } from 'app/plugins/openstack/components/storage/snapshots/helper'
import { imagesSelector } from 'app/plugins/openstack/components/images/selectors'
import { isVolumeBackedVm } from '../helpers'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  overview: {
    display: 'grid',
    gridTemplateColumns: '370px 1fr',
    marginTop: '16px',
    gridGap: '24px',
  },
  column: {
    display: 'grid',
    gridGap: '24px',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
  },
  flavorTooltip: {
    padding: 8,
  },
  tooltipIcon: {
    color: theme.palette.grey[300],
  },
  withTooltip: {
    display: 'grid',
    gap: 8,
    gridTemplateColumns: 'max-content max-content',
    alignItems: 'center',
  },
}))

const volumeTableColumns = [
  {
    key: 'name',
    label: 'Name',
    render: (val, volume) => {
      return (
        <SimpleLink
          src={routeManager.routes.openstack.volumeDetails.path({
            id: volume?.id,
            tab: IVolumeDetailsPageTabs.Overview,
          })}
        >
          {volume?.name || volume?.id}
        </SimpleLink>
      )
    },
  },
  { key: 'description', label: 'Description' },
  { key: 'attachments', label: 'Device', render: (val) => val?.[0]?.device },
  {
    key: 'snapshots',
    label: 'Snapshots',
    render: (snapshots) => snapshots?.length,
  },
  { key: 'size', label: 'Size', render: (val) => humanReadableSize(val * 1024 * 1024 * 1024) },
]

export default function Overview({ vm, loading, tasks, loadingTasks, volumes, loadingVolumes }) {
  const classes = useStyles({})
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  const [pathOnHost, setPathOnHost] = useState('')
  const [vmPorts, setVmPorts] = useState([])
  const [loadingVmPorts, setLoadingVmPorts] = useState(true)
  const [errorVm, setErrorVm] = useState<string>(null)

  const isVolumeBackedInstance = useMemo(() => {
    return isVolumeBackedVm(vm)
  }, [vm])

  const volumesById = useSelector(volumesByIdSelector)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)
  const vmsById = useSelector(virtualMachinesByIdSelector)
  const images = useSelector(imagesSelector)

  useEffect(() => {
    const getHypervisorInfo = async () => {
      const response: any = await getResmgrRoleConfig({
        role: isVmware ? 'pf9-ostackhost-neutron-vmw' : 'pf9-ostackhost-neutron',
        host: { id: vm?.['OS-EXT-SRV-ATTR:host'] },
      })
      setPathOnHost(response?.instances_path)
    }
    if (isAdmin() && !pathOnHost && vm?.['OS-EXT-SRV-ATTR:host']) {
      getHypervisorInfo()
    }
  }, [vm?.['OS-EXT-SRV-ATTR:host'], isVmware, pathOnHost])

  useEffect(() => {
    if (!vm?.id) {
      return
    }
    const getPorts = async () => {
      try {
        const response = await getPortsForVm({ vmId: vm.id })
        setVmPorts(response)
      } catch (e) {
        console.log(e)
      } finally {
        setLoadingVmPorts(false)
      }
    }
    getPorts()
  }, [vm?.id])

  const addressTableColumns = useMemo(
    () => [
      {
        key: 'networkName',
        label: 'Network',
      },
      { key: 'addr', label: 'IP Address' },
      { key: 'OS-EXT-IPS-MAC:mac_addr', label: 'MAC Address' },
      { key: 'version', label: 'Version' },
      {
        key: 'OS-EXT-IPS:type',
        label: 'Type',
      },
      {
        key: 'index',
        label: 'Security Groups',
        render: (val, item) => {
          // not sure if there could be duplicate mac addresses or not
          const port = vmPorts.find(
            (vmPort) => item['OS-EXT-IPS-MAC:mac_addr'] === vmPort.mac_address,
          )
          const securityGroupsString = port?.securityGroups?.map(
            (group) => group?.name || group?.id,
          )
          return securityGroupsString || 'N/A'
        },
      },
    ],
    [vmPorts],
  )

  const addresses = useMemo(() => {
    if (!vm?.addresses) {
      return []
    }
    const networkNames = Object.keys(vm?.addresses)
    return networkNames.reduce((accum, name) => {
      const networkAddresses = vm.addresses[name]
      const withNetworkName = networkAddresses.map((address, idx) => {
        return {
          ...address,
          networkName: name,
          index: idx,
        }
      })
      return [...accum, ...withNetworkName]
    }, [])
  }, [vm])

  const attachedVolumeSnapshots = useMemo(() => {
    if (!vm?.volumeIds) {
      return []
    }
    return vm?.volumeIds?.reduce((accum, volumeId) => {
      const volume = volumesById[volumeId]
      if (volume?.snapshots) {
        return [...accum, ...volume.snapshots]
      }
      return accum
    }, [])
  }, [vm, volumesById])

  const attachedVmSnapshots = useMemo(() => {
    // Get all vm snapshopts from images list
    const vmsnapshots = images?.filter((image) =>
      ['ephemeralDiskSnapshot', 'vmVolumeSnapshot'].includes(image?.imageType),
    )

    return vmsnapshots?.filter((snapshot) => snapshot?.instance_uuid === vm?.id) || []
  }, [vm])

  const overviewData = useMemo(() => {
    if (!vm) {
      return []
    }
    return [
      {
        key: 'ID',
        value: vm?.id,
      },
      {
        key: 'Host',
        value: vm?.['OS-EXT-SRV-ATTR:hypervisor_hostname'],
      },
      !isVolumeBackedInstance && {
        key: 'Host Disk Location',
        value: pathOnHost,
      },
      {
        key: 'Name on Host',
        value: vm?.['OS-EXT-SRV-ATTR:instance_name'],
      },
      {
        key: 'VM State',
        value: vm?.state,
        render: (item) => (
          <VmStateCellComponent item={item} state={vm?.state} setErrorVm={setErrorVm} />
        ),
      },
      {
        key: 'Lease Expiration',
        value: vm?.expirationTime,
        render: (value) => formatTableDate(value),
      },
      {
        key: 'Image',
        value: vm?.imageName,
      },
      {
        key: 'Security Groups',
        value: vm?.security_groups?.map((group) => group?.name).join(', '),
      },
      {
        key: 'Volume Deletion on VM Termination',
        value: !!vm?.['os-extended-volumes:volumes_attached']?.[0]?.delete_on_termination
          ? 'Enabled'
          : 'Disabled',
        condition: (item) => {
          return item?.['os-extended-volumes:volumes_attached']?.length
        },
      },
      {
        key: 'Metadata',
        value: vm?.metadata,
        render: (item) => {
          return !isNilOrEmpty(vm?.metadata) ? (
            <Labels labels={vm?.metadata} variant="default" showMoreButton ellipsisAt={75} />
          ) : (
            <Text variant="caption1">No Metadata</Text>
          )
        },
      },
      {
        key: 'Specs',
        value: vm?.flavor,
        render: (item) => {
          const flavor = item?.flavor
          const hotplugMemory = Number(item?.metadata?.['HOTPLUG_MEMORY'])
          const hotplugVcpus = Number(item?.metadata?.['HOTPLUG_CPU'])
          return (
            <div className={classes.withTooltip}>
              <Text variant="caption1">{flavor?.original_name}</Text>
              <Tooltip
                customBody={
                  <div className={classes.flavorTooltip}>
                    <div>
                      RAM:{' '}
                      <b>
                        {hotplugMemory
                          ? `${humanReadableSize(hotplugMemory * 1024 * 1024)} (hotplugged)`
                          : humanReadableSize(flavor?.ram * 1024 * 1024)}
                      </b>
                    </div>
                    <div>
                      vCPUs: <b>{hotplugVcpus ? `${hotplugVcpus} (hotplugged)` : flavor?.vcpus}</b>
                    </div>
                    <div>
                      Disk: <b>{humanReadableSize(flavor?.disk * 1024 * 1024 * 1024)}</b>
                    </div>
                  </div>
                }
              >
                <FontAwesomeIcon className={classes.tooltipIcon}>circle-question</FontAwesomeIcon>
              </Tooltip>
            </div>
          )
        },
      },
      {
        key: 'SSH Key',
        value: vm?.key_name || 'N/A',
      },
    ]
  }, [vm, pathOnHost])

  const volumeSnapshotTableColumns = useMemo(() => getVolumeSnapshotColumns({ volumesById }), [
    volumesById,
  ])

  const vmSnapshotColumns = useMemo(() => {
    return getVmSnapshotColumns({ vmsById, volumeSnapshotsById, hideKeys: ['id', 'instance_uuid'] })
  }, [vmsById, volumeSnapshotsById])

  return (
    <Progress loading={loading}>
      {errorVm && (
        <VmErrorModal
          hostName={vm?.['OS-EXT-SRV-ATTR:hypervisor_hostname']}
          vmId={errorVm}
          onClose={() => setErrorVm(null)}
        />
      )}
      <div className={classes.overview}>
        <div className={classes.column}>
          <OverviewCard item={vm} data={overviewData} icon="desktop" />
        </div>
        <div className={classes.column}>
          <TasksPage vm={vm} loading={loading} tasks={tasks} loadingTasks={loadingTasks} />
          <Card title="Networks" withCustomBody>
            <Grid
              uniqueIdentifier="addr"
              data={addresses}
              columns={addressTableColumns}
              loading={loading}
              compact
              disableToolbar
            />
          </Card>
          <Card title="Volumes" withCustomBody>
            <Grid
              uniqueIdentifier="id"
              data={volumes}
              columns={volumeTableColumns}
              loading={loadingVolumes}
              compact
              disableToolbar
            />
          </Card>
          {attachedVolumeSnapshots?.length ? (
            <Grid
              uniqueIdentifier="id"
              data={attachedVolumeSnapshots || []}
              columns={volumeSnapshotTableColumns}
              loading={loading}
              compact
              label="Volume Snapshots"
              showItemsCountInLabel={false}
            />
          ) : null}
          {attachedVmSnapshots?.length ? (
            <Grid
              uniqueIdentifier="id"
              data={attachedVmSnapshots || []}
              columns={vmSnapshotColumns}
              loading={loading}
              compact
              label="VM Snapshots"
              showItemsCountInLabel={false}
            />
          ) : null}
        </div>
      </div>
    </Progress>
  )
}
